/**
 * Only four of the six Firebase config values are actually load-bearing here.
 * The app uses Auth and Firestore, so a project with neither Storage nor Cloud
 * Messaging provisioned simply has no bucket or sender id to report, and must
 * still boot rather than showing the setup screen for a value it never reads.
 */
const REQUIRED = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
} as const

const OPTIONAL = {
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
} as const

const ENV_NAMES: Record<keyof typeof REQUIRED, string> = {
  apiKey: 'VITE_FIREBASE_API_KEY',
  authDomain: 'VITE_FIREBASE_AUTH_DOMAIN',
  projectId: 'VITE_FIREBASE_PROJECT_ID',
  appId: 'VITE_FIREBASE_APP_ID',
}

/** Names of the required config vars that are still blank — shown on the setup screen. */
export const missingFirebaseEnv: string[] = (Object.keys(REQUIRED) as (keyof typeof REQUIRED)[])
  .filter((key) => !REQUIRED[key])
  .map((key) => ENV_NAMES[key])

export const isFirebaseConfigured = missingFirebaseEnv.length === 0

export const firebaseConfig: Record<string, string> = {
  ...(Object.fromEntries(
    Object.entries({ ...REQUIRED, ...OPTIONAL }).filter(([, value]) => Boolean(value)),
  ) as Record<string, string>),
}

export const useEmulators: boolean = import.meta.env.VITE_USE_EMULATORS === 'true'
